/**
 * Utility functions for working with business listing tiers in MTS applications
 */

/**
 * Available listing tiers
 */
export const LISTING_TIERS = {
  FREE: 'FREE',
  BASIC: 'BASIC', 
  PREMIUM: 'PREMIUM',
  ELITE: 'ELITE'
};

/**
 * Tier configuration with features and limits
 */
export const TIER_CONFIG = {
  [LISTING_TIERS.FREE]: {
    name: 'Free',
    price: 0,
    billingPeriod: 'month',
    features: {
      basicListing: true,
      photos: 1,
      businessHours: true,
      contactInfo: true,
      socialMedia: false,
      customDescription: false,
      analytics: false,
      featuredPlacement: false,
      customBranding: false,
      multipleLocations: false,
      eventListing: false,
      promotionalPosts: 0,
      reviewResponses: false,
      advancedAnalytics: false,
      prioritySupport: false
    },
    limits: {
      maxPhotos: 1,
      maxDescriptionLength: 250,
      maxPromotionalPosts: 0,
      maxEvents: 0
    },
    color: 'gray',
    badgeColor: 'secondary',
    sortOrder: 1
  },
  
  [LISTING_TIERS.BASIC]: {
    name: 'Basic',
    price: 29.99,
    billingPeriod: 'month',
    features: {
      basicListing: true,
      photos: 5,
      businessHours: true,
      contactInfo: true,
      socialMedia: true,
      customDescription: true,
      analytics: true,
      featuredPlacement: false,
      customBranding: false,
      multipleLocations: false,
      eventListing: true,
      promotionalPosts: 2,
      reviewResponses: true,
      advancedAnalytics: false,
      prioritySupport: false
    },
    limits: {
      maxPhotos: 5,
      maxDescriptionLength: 500,
      maxPromotionalPosts: 2,
      maxEvents: 5
    },
    color: 'blue',
    badgeColor: 'primary',
    sortOrder: 2
  },
  
  [LISTING_TIERS.PREMIUM]: {
    name: 'Premium',
    price: 59.99,
    billingPeriod: 'month',
    features: {
      basicListing: true,
      photos: 15,
      businessHours: true,
      contactInfo: true,
      socialMedia: true,
      customDescription: true,
      analytics: true,
      featuredPlacement: true,
      customBranding: true,
      multipleLocations: true,
      eventListing: true,
      promotionalPosts: 10,
      reviewResponses: true,
      advancedAnalytics: true,
      prioritySupport: false
    },
    limits: {
      maxPhotos: 15,
      maxDescriptionLength: 1000,
      maxPromotionalPosts: 10,
      maxEvents: 20,
      maxLocations: 3
    },
    color: 'yellow',
    badgeColor: 'warning',
    sortOrder: 3
  },
  
  [LISTING_TIERS.ELITE]: {
    name: 'Elite',
    price: 99.99,
    billingPeriod: 'month',
    features: {
      basicListing: true,
      photos: -1, // Unlimited
      businessHours: true,
      contactInfo: true,
      socialMedia: true,
      customDescription: true,
      analytics: true,
      featuredPlacement: true,
      customBranding: true,
      multipleLocations: true,
      eventListing: true,
      promotionalPosts: -1, // Unlimited
      reviewResponses: true,
      advancedAnalytics: true,
      prioritySupport: true
    },
    limits: {
      maxPhotos: -1, // Unlimited
      maxDescriptionLength: 2000,
      maxPromotionalPosts: -1, // Unlimited
      maxEvents: -1, // Unlimited
      maxLocations: -1 // Unlimited
    },
    color: 'purple',
    badgeColor: 'purple',
    sortOrder: 4
  }
};

/**
 * Get tier configuration
 * @param {string} tier - Tier name
 * @returns {object} Tier configuration object
 */
export const getTierConfig = (tier) => {
  return TIER_CONFIG[tier] || TIER_CONFIG[LISTING_TIERS.FREE];
};

/**
 * Get tier display name
 * @param {string} tier - Tier name
 * @returns {string} Human-readable tier name
 */
export const getTierDisplayName = (tier) => {
  const config = getTierConfig(tier);
  return config.name;
};

/**
 * Get tier price
 * @param {string} tier - Tier name
 * @returns {number} Tier price
 */
export const getTierPrice = (tier) => {
  const config = getTierConfig(tier);
  return config.price;
};

/**
 * Get tier color for UI components
 * @param {string} tier - Tier name
 * @returns {string} Color variant name
 */
export const getTierColor = (tier) => {
  const config = getTierConfig(tier);
  return config.badgeColor;
};

/**
 * Check if a tier has a specific feature
 * @param {string} tier - Tier name
 * @param {string} feature - Feature name
 * @returns {boolean} True if tier has feature
 */
export const tierHasFeature = (tier, feature) => {
  const config = getTierConfig(tier);
  return Boolean(config.features[feature]);
};

/**
 * Get limit for a specific resource in a tier
 * @param {string} tier - Tier name
 * @param {string} resource - Resource name (e.g., 'maxPhotos')
 * @returns {number} Limit (-1 for unlimited)
 */
export const getTierLimit = (tier, resource) => {
  const config = getTierConfig(tier);
  return config.limits[resource] || 0;
};

/**
 * Check if tier allows unlimited for a resource
 * @param {string} tier - Tier name
 * @param {string} resource - Resource name
 * @returns {boolean} True if unlimited
 */
export const isTierUnlimited = (tier, resource) => {
  return getTierLimit(tier, resource) === -1;
};

/**
 * Check if current usage exceeds tier limit
 * @param {string} tier - Tier name
 * @param {string} resource - Resource name
 * @param {number} currentUsage - Current usage count
 * @returns {boolean} True if usage exceeds limit
 */
export const isUsageExceedingLimit = (tier, resource, currentUsage) => {
  const limit = getTierLimit(tier, resource);
  if (limit === -1) return false; // Unlimited
  return currentUsage >= limit;
};

/**
 * Get remaining quota for a resource
 * @param {string} tier - Tier name
 * @param {string} resource - Resource name
 * @param {number} currentUsage - Current usage count
 * @returns {number|string} Remaining quota or 'unlimited'
 */
export const getRemainingQuota = (tier, resource, currentUsage) => {
  const limit = getTierLimit(tier, resource);
  if (limit === -1) return 'unlimited';
  return Math.max(0, limit - currentUsage);
};

/**
 * Get usage percentage for a resource
 * @param {string} tier - Tier name
 * @param {string} resource - Resource name
 * @param {number} currentUsage - Current usage count
 * @returns {number} Usage percentage (0-100)
 */
export const getUsagePercentage = (tier, resource, currentUsage) => {
  const limit = getTierLimit(tier, resource);
  if (limit === -1) return 0; // Unlimited
  if (limit === 0) return 100;
  return Math.min(100, (currentUsage / limit) * 100);
};

/**
 * Get all tiers sorted by priority
 * @returns {Array} Array of tier objects
 */
export const getAllTiers = () => {
  return Object.entries(TIER_CONFIG)
    .map(([key, config]) => ({ key, ...config }))
    .sort((a, b) => a.sortOrder - b.sortOrder);
};

/**
 * Get upgrade suggestions for current tier
 * @param {string} currentTier - Current tier
 * @returns {Array} Array of upgrade options
 */
export const getUpgradeOptions = (currentTier) => {
  const currentConfig = getTierConfig(currentTier);
  const allTiers = getAllTiers();
  
  return allTiers.filter(tier => tier.sortOrder > currentConfig.sortOrder);
};

/**
 * Calculate tier upgrade cost
 * @param {string} fromTier - Current tier
 * @param {string} toTier - Target tier
 * @param {number} daysRemaining - Days remaining in current billing period
 * @returns {object} Cost breakdown
 */
export const calculateUpgradeCost = (fromTier, toTier, daysRemaining = 30) => {
  const fromConfig = getTierConfig(fromTier);
  const toConfig = getTierConfig(toTier);
  
  const priceDifference = toConfig.price - fromConfig.price;
  const proratedAmount = (priceDifference * daysRemaining) / 30;
  const immediateCharge = Math.max(0, proratedAmount);
  const nextBillingAmount = toConfig.price;
  
  return {
    priceDifference,
    proratedAmount: Math.round(immediateCharge * 100) / 100,
    nextBillingAmount,
    savings: fromConfig.price > 0 ? `Save $${(fromConfig.price - toConfig.price).toFixed(2)}/month` : null
  };
};

/**
 * Check if tier is valid
 * @param {string} tier - Tier to validate
 * @returns {boolean} True if valid tier
 */
export const isValidTier = (tier) => {
  return Object.keys(LISTING_TIERS).includes(tier);
};

/**
 * Get tier comparison data for pricing tables
 * @param {Array} tierKeys - Array of tier keys to compare (default: all)
 * @returns {Array} Comparison data
 */
export const getTierComparison = (tierKeys = Object.keys(LISTING_TIERS)) => {
  return tierKeys.map(tierKey => {
    const config = getTierConfig(tierKey);
    return {
      key: tierKey,
      name: config.name,
      price: config.price,
      billingPeriod: config.billingPeriod,
      color: config.color,
      badgeColor: config.badgeColor,
      features: config.features,
      limits: config.limits,
      sortOrder: config.sortOrder
    };
  }).sort((a, b) => a.sortOrder - b.sortOrder);
};

/**
 * Get feature comparison matrix
 * @returns {object} Feature matrix with tiers and features
 */
export const getFeatureMatrix = () => {
  const tiers = getAllTiers();
  const allFeatures = new Set();
  
  // Collect all unique features
  tiers.forEach(tier => {
    Object.keys(tier.features).forEach(feature => allFeatures.add(feature));
  });
  
  const featureList = Array.from(allFeatures);
  
  return {
    tiers: tiers.map(tier => ({ key: tier.key, name: tier.name, color: tier.badgeColor })),
    features: featureList.map(feature => ({
      key: feature,
      name: formatFeatureName(feature),
      tiers: tiers.reduce((acc, tier) => {
        acc[tier.key] = tier.features[feature];
        return acc;
      }, {})
    }))
  };
};

/**
 * Format feature name for display
 * @param {string} feature - Feature key
 * @returns {string} Formatted feature name
 */
const formatFeatureName = (feature) => {
  const nameMap = {
    basicListing: 'Basic Listing',
    photos: 'Photo Gallery',
    businessHours: 'Business Hours',
    contactInfo: 'Contact Information',
    socialMedia: 'Social Media Links',
    customDescription: 'Custom Description',
    analytics: 'Basic Analytics',
    featuredPlacement: 'Featured Placement',
    customBranding: 'Custom Branding',
    multipleLocations: 'Multiple Locations',
    eventListing: 'Event Listings',
    promotionalPosts: 'Promotional Posts',
    reviewResponses: 'Review Responses',
    advancedAnalytics: 'Advanced Analytics',
    prioritySupport: 'Priority Support'
  };
  
  return nameMap[feature] || feature.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
};